import { Fragment, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  type BoxProps,
  Grid,
  Input,
  Text,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldRoot } from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  useListSingularGroupNames,
  useSaveSingularGroupNames,
} from '~/pages/AdminSingularDay/hooks/useSingularGroupNames'
import {
  SINGULAR_DAY_SPECIAL_GROUP_NUMBERS,
  type SingularGroupNameErrorKind,
  buildSingularGroupNameMap,
  effectiveSingularGroupName,
  validateSingularGroupNames,
} from '~/pages/AdminSingularDay/models/options'

// Text の as="label" は htmlFor prop を型に持たない (`SingularDayForm.tsx`
// の `GridFormLabel` と同じ制約)。 同型の Box wrapper で回避する。
const BoxAsLabel = Box as unknown as React.FC<
  Omit<BoxProps, 'as'> & {
    as?: string
    htmlFor?: string
    children?: React.ReactNode
  }
>

const ERROR_MESSAGE_KEY: Record<SingularGroupNameErrorKind, string> = {
  tooLong: 'page:adminSingularDay.serverError.groupNameTooLong',
  duplicate: 'page:adminSingularDay.serverError.groupNameDuplicate',
  sameAsBehavior: 'page:adminSingularDay.serverError.groupNameSameAsBehavior',
}

type Props = {
  isOpen: boolean
  onClose: () => void
}

/** グループ番号 → 入力値の snapshot を保存済みの map から組み立てる。 */
function snapshotFrom(
  namesByGroup: ReadonlyMap<number, string>,
): Record<number, string> {
  return Object.fromEntries(
    SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.map((n) => [
      n,
      namesByGroup.get(n) ?? '',
    ]),
  )
}

/**
 * 特異グループ名の一括編集モーダル (AC-MSD-011d、design
 * `special-day-calendar.html` の `#sdc-gname-modal` 準拠)。
 *
 * `ListSingularGroupNames` を自前で fetch する (index.tsx の一覧表示用
 * `groupNames` とは独立の loaded snapshot を持つ)。 これは 2 点の事故を防ぐ
 * ため:
 *
 *  1. **未ロードでの data loss**: 呼び出し元が list query 未解決のまま空
 *     Map を渡すと、 全 10 group が「未命名」に見えて保存すると命名済み
 *     group が全削除される。 本 component は `isLoading` の間 入力を
 *     disabled にし保存を抑止する (loaded snapshot が確定するまで開かせない
 *     のと同値)。
 *  2. **D2 逸脱 (full-state 送信)**: 1〜10 全件を毎回送ると、 モーダルを
 *     開いている間に別セッションが命名した group を、 このモーダルの
 *     空欄 (未編集) スロットの DELETE で silent に消してしまう
 *     (D5 の「per-group upsert は変更した group だけに触れる」という
 *     clobber-free 前提を破る)。 保存は mount 時の loaded snapshot と
 *     現在値を比較し、 **変更のあった group の entry のみ** を送る
 *     (per-group upsert batch、design Note D2)。
 */
export const GroupNamesModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { data, isError } = useListSingularGroupNames()
  const { mutateAsync, isPending } = useSaveSingularGroupNames()
  const [snapshot, setSnapshot] = useState<Record<number, string> | undefined>(
    undefined,
  )
  const [values, setValues] = useState<Record<number, string>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({})
  const [serverError, setServerError] = useState<string | undefined>(undefined)

  // mount 時 (かつ data 解決後) に 1 度だけ loaded snapshot を確定する。
  // 以後 list query が背後で再 fetch されても snapshot は変えない
  // (open 中の diff 基準がすり替わるのを防ぐ)。
  useEffect(() => {
    if (data !== undefined && snapshot === undefined) {
      const snap = snapshotFrom(
        buildSingularGroupNameMap(data.singularGroupNames),
      )
      setSnapshot(snap)
      setValues(snap)
    }
  }, [data, snapshot])

  const handleClose = (): void => {
    if (snapshot !== undefined) {
      setValues(snapshot)
    }
    setFieldErrors({})
    setServerError(undefined)
    onClose()
  }

  const handleSave = async (): Promise<void> => {
    if (snapshot === undefined) {
      // loaded snapshot 未確定 (isLoading または fetch 未完了): 保存を抑止する。
      return
    }

    // 変更のあった group のみ抽出 (D2 per-group upsert batch。
    // 未変更 group は payload から除外し、 モーダル外の並行更新を
    // silent 上書きしない)。
    // 自分の番号の既定名は「名前なし」(空) として扱う (AC-MSD-020)。
    const changedGroups = SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.filter(
      (n) =>
        effectiveSingularGroupName(n, values[n] ?? '') !== (snapshot[n] ?? ''),
    )
    if (changedGroups.length === 0) {
      onClose()
      return
    }

    // 検証は全 10 欄の現在値に対して行う (重複は未変更欄との間でも起きる。
    // AC-MSD-018 / 019)。 送信対象 (changedGroups) とは独立。
    const nextFieldErrors: Record<number, string> = {}
    for (const [n, kind] of Object.entries(
      validateSingularGroupNames(values),
    )) {
      nextFieldErrors[Number(n)] = t(ERROR_MESSAGE_KEY[kind])
    }
    setFieldErrors(nextFieldErrors)
    if (Object.keys(nextFieldErrors).length > 0) {
      return
    }
    setServerError(undefined)
    try {
      await mutateAsync({
        entries: changedGroups.map((n) => ({
          singularGroup: n,
          name: effectiveSingularGroupName(n, values[n] ?? ''),
        })),
      })
      onClose()
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({
        title: t('page:adminSingularDay.toast.groupNamesSaved'),
      })
    } catch {
      setServerError(t('page:adminSingularDay.serverError.unknown'))
    }
  }

  const footer = (
    <SaveCancelFooter
      isPending={isPending}
      isSaveDisabled={snapshot === undefined}
      onCancel={handleClose}
      onSave={() => {
        void handleSave()
      }}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={handleClose}
      /* [2026-09-17 / Des 指摘] 8080 は `ecl-modal--sm` だが、 グループ名は
         「GW 中日」 のような短い語だけでなく説明的な名前も入る。 sm のままだと
         ラベル列 (max-content) を引いた残りが狭く、 数文字で横スクロールになる。
         入力欄を横いっぱいに取りたいので、 親の特異日登録モーダルと同じ lg まで広げる
         (md では目視でまだ右に余白が残っていた)。 入力欄が実際に伸びるかは
         `FieldRoot` の alignItems="stretch" が効いているかに依存する (下記)。 */
      size="lg"
      title={t('page:adminSingularDay.modal.groupNamesTitle')}
    >
      {serverError !== undefined && (
        <Alert status="error" title={serverError} />
      )}
      {/* loaded snapshot が確定するまでフォームを描画しない (= 解決まで
          開かせない、上の JSDoc「未ロードでの data loss」参照)。 disabled
          input を出す形にすると「表示はされるが空欄」という中間状態が
          生まれ誤操作を誘発しうるため、 loading 表示に倒す。 取得が恒久失敗
          (リトライ後も error) した時は「読み込み中」で固まらないよう失敗を
          明示する (モーダルを開き直すと query が再実行され再試行になる)。 */}
      {snapshot === undefined ? (
        isError ? (
          <Alert
            description={t('page:adminSingularDay.serverError.loadFailedDesc')}
            status="error"
            title={t('page:adminSingularDay.serverError.loadFailedTitle')}
          />
        ) : (
          <Box role="status">
            {t('page:adminSingularDay.modal.groupNamesLoading')}
          </Box>
        )
      ) : (
        <Grid
          alignItems="center"
          gridRowGap={3}
          gridColumnGap={3}
          gridTemplateColumns="max-content 1fr"
        >
          {/* 列見出し (AC-MSD-020、 design `.sdc-gname-head`)。 視覚のみ:
              各入力は左の既定名を label に持つので読み上げない。 */}
          <Text textStyle="captionStrong" color="Text.midEmphasis" aria-hidden>
            {t('page:adminSingularDay.modal.groupNamesDefaultHeader')}
          </Text>
          <Text textStyle="captionStrong" color="Text.midEmphasis" aria-hidden>
            {t('page:adminSingularDay.modal.groupNamesDisplayHeader')}
          </Text>
          {SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.map((n) => (
            <Fragment key={n}>
              <BoxAsLabel
                as="label"
                textStyle="label"
                color="Text.highEmphasis"
                htmlFor={`gname-${n}`}
              >
                {t('page:adminSingularDay.demandBehavior.singularGroupName', {
                  n,
                })}
              </BoxAsLabel>
              {/* [2026-09-17 / Des 指摘] `alignItems="stretch"` が要る。 DS `Input` は
                  内部を縦 Stack + `Field.Root` で包むが、 chakra の `Field.Root` は
                  `align-items: flex-start` なので Stack がセル幅まで伸びず内容幅で止まり、
                  `width="full"` を付けても入力欄が 1fr 列を埋めない (`SingularDayForm`
                  の名称欄・需要扱いと同じ事象)。 モーダルを広げても入力欄だけが
                  取り残されて右に余白が残っていた。 */}
              <FieldRoot
                alignItems="stretch"
                invalid={fieldErrors[n] !== undefined}
              >
                <Input
                  width="full"
                  aria-label={t(
                    'page:adminSingularDay.demandBehavior.singularGroupName',
                    { n },
                  )}
                  id={`gname-${n}`}
                  onChange={(e) => {
                    setValues((prev) => ({ ...prev, [n]: e.target.value }))
                    setFieldErrors((prev) => {
                      const next = { ...prev }
                      delete next[n]
                      return next
                    })
                  }}
                  placeholder={t(
                    'page:adminSingularDay.demandBehavior.singularGroupName',
                    { n },
                  )}
                  value={values[n] ?? ''}
                />
                {fieldErrors[n] !== undefined && (
                  <FieldErrorText>{fieldErrors[n]}</FieldErrorText>
                )}
              </FieldRoot>
            </Fragment>
          ))}
        </Grid>
      )}
    </Modal>
  )
}
